import { useState, useRef, useCallback, useMemo, useEffect } from "react";
import { isEmpty } from "@util/utils";
import { isAllMerchandiseMenu, isFeeCategory, filterItemsByKeyword } from "@icoach/accountBook/createOrder/CreateOrderPage.util";

/**
 * 商品數據管理 Hook
 * 處理分類選單的商品數據獲取、分類切換狀態和列表過濾
 * @param {Function} getProductSaleOptionsApi - 獲取分類商品的 API 函式
 * @param {string} searchKeyword - 搜尋關鍵字（來自 useProductSearch）
 * @param {Object} currentMenu - 當前選中的分類選單項
 * @param {Function} setCurrentMenu - 設置當前分類的函式
 * @param {Function} resetSearch - 重置搜尋的函式（來自 useProductSearch）
 * @param {Function} searchAllMerchandiseWithDebounce - 全商品搜尋函式（來自 useProductSearch）
 * @returns {Object} 商品數據相關的狀態和方法
 */
export const useProductData = (getProductSaleOptionsApi, searchKeyword, currentMenu, setCurrentMenu, resetSearch, searchAllMerchandiseWithDebounce) => {
	const [products, setProducts] = useState([]); // 當前顯示的商品列表
	const productTypeTextRef = useRef(""); // 用於購物車項目的分類名稱

	// 載入當前分類下的商品列表
	const loadProducts = useCallback(
		async menuItem => {
			if (!menuItem) return;

			if (isFeeCategory(menuItem)) {
				setProducts(menuItem.checkoutItems); // 費用：直接使用
			} else {
				try {
					const res = await getProductSaleOptionsApi({ saleCateID: menuItem.value });
					setProducts(res || []);
				} catch (error) {
					console.error("載入商品列表時發生錯誤:", error);
					setProducts([]);
				}
			}
		},
		// eslint-disable-next-line
		[getProductSaleOptionsApi],
	);

	// 處理分類點擊 (供 ShoppingMenu 使用)
	const handleClickMenu = useCallback(
		(item, productTypeText) => {
			resetSearch(); // 重置搜尋相關狀態 (來自 useProductSearch)
			setProducts([]); // 清空當前產品列表
			productTypeTextRef.current = productTypeText;
			setCurrentMenu(item);
		},
		// eslint-disable-next-line
		[resetSearch, setCurrentMenu],
	);

	// 根據關鍵字過濾商品
	const filterProducts = useMemo(
		() => {
			// "ALL" 分類下的 products 是後端 API 搜尋結果，無需再過濾
			if (isAllMerchandiseMenu(currentMenu)) {
				return products;
			}
			// 非 "ALL" 分類：需要本地過濾（包含商品和費用）
			return filterItemsByKeyword(products, searchKeyword);
		},
		// eslint-disable-next-line
		[products, searchKeyword, currentMenu],
	);

	// 副作用：根據分類類型處理商品載入
	// 全商品分類：使用搜尋功能；非全商品分類：載入分類商品列表
	useEffect(
		() => {
			if (isEmpty(currentMenu)) return;

			// 全商品分類：使用搜尋功能
			if (isAllMerchandiseMenu(currentMenu)) {
				if (isEmpty(searchKeyword)) {
					setProducts([]); // 全商品分類且無搜尋關鍵字時，不載入任何商品
					return;
				}

				// 使用 searchAllMerchandiseWithDebounce 進行搜尋
				let isCancelled = false;

				searchAllMerchandiseWithDebounce(searchKeyword).then(results => {
					if (!isCancelled) {
						setProducts(results || []);
					}
				});

				return () => {
					isCancelled = true;
				};
			} else {
				// 非全商品分類：載入該分類的商品列表
				loadProducts(currentMenu);
			}
		},
		// eslint-disable-next-line
		[currentMenu, searchKeyword, searchAllMerchandiseWithDebounce, loadProducts],
	);

	return {
		products,
		setProducts, // 提供給外部使用（如果需要）
		productTypeTextRef,
		filterProducts,
		handleClickMenu,
		loadProducts,
	};
};
